import { Form, useFormContext } from "@/components/form";
import { FormFieldWithError } from "@/components/form/form-field-with-error";
import { Stack, InputAdornment, IconButton, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Dialog, ModalContextExtractor, ModalHeader, useOpenModal } from "@/components/modal";
import FacturaManualSearch from "@/features/facturacion-electronica/components/factura-manual-search";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import dayjs from 'dayjs';
import { getDEByCDC } from "../../api/get-de-by-cdc";
import { notify } from "@/shared/notification";
import { DETipoConstanciaAsociadoEnum, DETipoDocumentoAsociadoEnum, LENGTH_CDC } from "@/shared/types/documento-electronico";
import { Cliente } from "@/shared/types/cliente";

interface DocumentoAsociadoFieldsProps {
    onClienteLoad?: (cliente: Cliente | null) => void;
    cdcLoaded?: boolean;
    setCdcLoaded: (loaded: boolean) => void;
    handleReset?: () => void;
    forNotaCredito?: boolean;
    forAutofactura?: boolean;
    opcional?: boolean;
}

export default function DocumentoAsociadoFields({ onClienteLoad, cdcLoaded, setCdcLoaded, handleReset,
    forNotaCredito = false, forAutofactura = false, opcional = false }: DocumentoAsociadoFieldsProps) {
    const form = useFormContext();
    const openModal = useOpenModal();
    const [loadingCDC, setLoadingCDC] = useState(false);

    // evitar warning de muix por fecha
    useEffect(() => {
        if (!form.getFieldValue("tipoDocumentoAsociado")) {
            form.setFieldValue("tipoDocumentoAsociado", "1");
        }

        if (!form.getFieldValue("tipoConstancia")) {
            form.setFieldValue("tipoConstancia", "1");
        }

        const currentDate = form.getFieldValue("fechaEmisionDocImpAsociado");
        if (currentDate === undefined) {
            form.setFieldValue("fechaEmisionDocImpAsociado", dayjs().toDate());
        }

        if (!form.getFieldValue("tipoComprobanteImpreso")) {
            form.setFieldValue("tipoComprobanteImpreso", 1);
        }
    }, []);

    const loadDEDataByCDC = async (cdc: string) => {
        if (!cdc || cdc.length !== LENGTH_CDC) return;

        setLoadingCDC(true);
        setCdcLoaded(false);

        try {
            const detallesAsociadoFE = await getDEByCDC(cdc);

            // Cargar datos básicos del documento
            form.setFieldValue("tipoTransaccion", detallesAsociadoFE.tipoTransaccion || "");
            form.setFieldValue("cMoneOpe", detallesAsociadoFE.cMoneOpe || "");
            form.setFieldValue("dTiCam", detallesAsociadoFE.dTiCam || 0);

            // Cargar items del documento electrónico
            if (detallesAsociadoFE.items && detallesAsociadoFE.items.length > 0) {
                form.setFieldValue("items", detallesAsociadoFE.items.map(item => ({
                    codigo: item.codigo || "",
                    descripcion: item.descripcion || "",
                    cantidad: item.cantidad || 0,
                    precio: item.precio || 0,
                    iva: String(item.iva),
                    unidadMedida: item.unidadMedida,
                })));
            }

            // Cargar contribuyente si existe el callback
            if (detallesAsociadoFE.cliente) {
                const cliente = detallesAsociadoFE.cliente;

                // Siempre setear el ID del receptor en el formulario
                form.setFieldValue("clienteId", cliente.id);

                // Notificar al caller si necesita poblar datos adicionales del contribuyente
                if (onClienteLoad) {
                    onClienteLoad(cliente);
                }
            }

            setCdcLoaded(true);
            notify("Datos del documento cargados correctamente", { variant: "success" });
        } catch (error) {
            console.error("Error al cargar datos del DE:", error);
            notify("Error al cargar datos del documento electrónico", { variant: "error" });
        } finally {
            setLoadingCDC(false);
        }
    };

    const handleFacturaSelect = (factura: any) => {
        handleReset?.();
        form.setFieldValue("cdcDocumentoAsociado", factura.cdc || "");
        if (factura.cdc) {
            loadDEDataByCDC(factura.cdc);
        }
    };

    return (
        <CollapsibleSection title="Documento Asociado">
            <form.Field name="tipoDocumentoAsociado">
                {(tipoField) => {
                    const tipoDocumento = tipoField.state.value || (opcional ? "none" : "1");

                    return (
                        <Stack gap={2}>
                            <Stack minWidth="200px">
                                <Form.RadioGroup
                                    name="tipoDocumentoAsociado"
                                    label="Tipo de Documento"
                                    row
                                >
                                    {opcional && <Form.RadioButton value="none" label="Ninguno" />}
                                    <Form.RadioButton value="1" label="Electrónico" disabled={forAutofactura} />
                                    <Form.RadioButton value="2" label="Impreso" disabled={cdcLoaded || forAutofactura} />
                                    <Form.RadioButton value="3" label="Constancia Electrónica" disabled={cdcLoaded || forNotaCredito} />
                                </Form.RadioGroup>
                            </Stack>

                            {tipoDocumento === DETipoDocumentoAsociadoEnum.Electronico && (
                                <Stack width={{ xs: "100%", sm: "500px" }}>
                                    <form.Field name="cdcDocumentoAsociado">
                                        {(field) => (
                                            <Form.TextField
                                                name="cdcDocumentoAsociado"
                                                label="CDC del Documento Asociado"
                                                size="small"
                                                placeholder="00000000000000000000000000000000000000000000"
                                                disabled={true}
                                                onBlur={() => {
                                                    const cdc = field.state.value;
                                                    if (cdc && cdc.length === LENGTH_CDC) {
                                                        loadDEDataByCDC(cdc);
                                                    }
                                                }}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                {loadingCDC && <CircularProgress size={20} />}
                                                                {cdcLoaded && !loadingCDC && (
                                                                    <IconButton
                                                                        onClick={() => handleReset && handleReset()}
                                                                        edge="end"
                                                                        size="small"
                                                                        color="error"
                                                                    >
                                                                        <HighlightOffIcon />
                                                                    </IconButton>
                                                                )}
                                                                <IconButton
                                                                    onClick={() => openModal("searchFactura")}
                                                                    edge="end"
                                                                    size="small"
                                                                >
                                                                    <SearchIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }
                                                }}
                                            />
                                        )}
                                    </form.Field>
                                </Stack>
                            )}

                            {tipoDocumento === DETipoDocumentoAsociadoEnum.Impreso && (
                                <Stack direction="row" gap={1} flexWrap="wrap" alignItems="flex-start">
                                    <FormFieldWithError
                                        name="timbradoDocImpAsociado"
                                        label="Timbrado"
                                        placeholder="12345678"
                                        inputMode="numeric"
                                        width={{ xs: '100%', sm: '180px' }}
                                    />

                                    <FormFieldWithError
                                        name="establecimientoDocImpAsociado"
                                        label="Est."
                                        placeholder="001"
                                        inputMode="numeric"
                                        width={{ xs: '48%', sm: '160px' }}
                                    />

                                    <FormFieldWithError
                                        name="puntoExpedicionDocImpAsociado"
                                        label="P. Exp."
                                        placeholder="001"
                                        inputMode="numeric"
                                        width={{ xs: '48%', sm: '160px' }}
                                    />

                                    <FormFieldWithError
                                        name="numeroDocImpAsociado"
                                        label="Número"
                                        placeholder="1234567"
                                        inputMode="numeric"
                                        width={{ xs: '100%', sm: '180px' }}
                                    />

                                    <Stack width={{ xs: '100%', sm: '200px' }}>
                                        <Form.Select
                                            name="tipoComprobanteImpreso"
                                            label="Tipo Comprobante"
                                            size="small"
                                        >
                                            <Form.Select.Option value={1}>Factura</Form.Select.Option>
                                            {!forNotaCredito && (<><Form.Select.Option value={2}>Nota de Crédito</Form.Select.Option>
                                                <Form.Select.Option value={3}>Nota de Débito</Form.Select.Option>
                                                <Form.Select.Option value={4}>Remisión</Form.Select.Option>
                                                <Form.Select.Option value={5}>Comprobante de retención</Form.Select.Option></>)}
                                        </Form.Select>
                                    </Stack>

                                    <Stack width={{ xs: '100%', sm: '150px' }}>
                                        <Form.DatePicker
                                            name="fechaEmisionDocImpAsociado"
                                            label="Fecha Emisión"
                                            size="small"
                                            defaultValue={dayjs()}
                                        />
                                    </Stack>
                                </Stack>
                            )}

                            {tipoDocumento === DETipoDocumentoAsociadoEnum.ConstanciaElectronica && (
                                <Stack direction="row" gap={1} flexWrap="wrap">
                                    <form.Field name="tipoConstancia">
                                        {(field) => {
                                            const handleChange = (value: string) => {
                                                field.handleChange(value);
                                                // Limpiar campos si se selecciona "De no ser contribuyente"
                                                if (value === DETipoConstanciaAsociadoEnum.DeNoSerContribuyente) {
                                                    setTimeout(() => {
                                                        form.setFieldValue("numeroConstancia", "");
                                                        form.setFieldValue("numeroControl", "");
                                                        form.resetField("numeroConstancia");
                                                        form.resetField("numeroControl");
                                                    }, 0);
                                                }
                                            };

                                            return (
                                                <Stack width={{ xs: '100%', sm: '250px' }}>
                                                    <Form.Select
                                                        name="tipoConstancia"
                                                        label="Tipo de Constancia"
                                                        size="small"
                                                        onValueChange={(e) => handleChange(e)}
                                                    >
                                                        <Form.Select.Option value="1">De no ser contribuyente</Form.Select.Option>
                                                        <Form.Select.Option value="2">De microproductores</Form.Select.Option>
                                                    </Form.Select>
                                                </Stack>
                                            );
                                        }}
                                    </form.Field>

                                    <form.Field name="tipoConstancia">
                                        {(tipoConstanciaField) => {
                                            const isDisabled = tipoConstanciaField.state.value === DETipoConstanciaAsociadoEnum.DeNoSerContribuyente;

                                            return (
                                                <>
                                                    <FormFieldWithError
                                                        name="numeroConstancia"
                                                        label="Número de Constancia"
                                                        placeholder="12345678911"
                                                        inputMode="numeric"
                                                        disabled={isDisabled}
                                                        customError={isDisabled ? null : undefined}
                                                        width={{ xs: '100%', sm: '200px' }}
                                                    />

                                                    <FormFieldWithError
                                                        name="numeroControl"
                                                        label="Número de Control"
                                                        placeholder="ABCD1234"
                                                        disabled={isDisabled}
                                                        customError={isDisabled ? null : undefined}
                                                        width={{ xs: '100%', sm: '200px' }}
                                                    />
                                                </>
                                            );
                                        }}
                                    </form.Field>
                                </Stack>
                            )}
                        </Stack>
                    );
                }}
            </form.Field>

            <Dialog code="searchFactura" size="lg">
                <ModalHeader>Buscar Documento Electrónico</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => (
                        <FacturaManualSearch
                            padding={3}
                            onFacturaSelect={(factura: any) => {
                                handleFacturaSelect(factura);
                                handleClose();
                            }}
                        />
                    )}
                </ModalContextExtractor>
            </Dialog>
        </CollapsibleSection>
    );
}

